import type { ReactElement } from "react";
import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { StyledIconButton } from "app/pages/sales-intelligence/common-components/filters-panel/components/FiltersComplexChip/styles";
import { FilterChipVariant } from "app/pages/sales-intelligence/common-components/filters-panel/types/commonEnums";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";

import { useInjectedVisitsFromSingleCountry } from "../../../context/VisitsFromSingleCountryContext";
import { toSingleVisitsFromCountry } from "../../../store/utils/selectCombiners";
import { MAX_CHIPS_GROUP_AMOUNT } from "../../../constants";
import type VisitsFromFilterModel from "../../../models/VisitsFromFilter/VisitsFromFilterModel";

import AggregatedDataCheckbox from "./components/AggregatedDataCheckbox/AggregatedDataCheckbox";
import VisitsFromSelectionModal from "./components/VisitsFromSelectionModal/VisitsFromSelectionModal";
import { StyledVisitsFromResultContainer, StyledChipContainer } from "./styles";

const VisitsFromFilterResult = ({
    value,
    filter,
    isActionEnabled = true,
    isEditEnabled,
    onFilterUpdate,
}: IFilterViewBaseProps<VisitsFromFilterModel>): ReactElement | null => {
    const { translate } = useTranslate();
    const isSingleCountryMode = useInjectedVisitsFromSingleCountry() ?? false;

    const [isModalOpened, setIsModalOpened] = useState(false);

    const { countriesById, regionsById } = filter;
    const resolvedValue = value ?? filter.getInitialDtoValue();
    const chipItems: readonly ChipItem[] = useMemo(
        () =>
            resolvedValue.reduce<ChipItem[]>((chips, id) => {
                const country = countriesById[id];
                const region = regionsById[id];
                const text = country?.text ?? region?.name;

                if (!text) {
                    return chips;
                }

                return [
                    ...chips,
                    {
                        id: String(id),
                        text,
                        variant: country ? FilterChipVariant.Country : FilterChipVariant.Default,
                    },
                ];
            }, []),
        [resolvedValue, countriesById, regionsById],
    );

    const handleCloseModal = () => {
        setIsModalOpened(false);
    };

    const handleSelectionApply = (newSelectedIds: readonly number[]) => {
        onFilterUpdate(isSingleCountryMode ? toSingleVisitsFromCountry(newSelectedIds) : newSelectedIds);

        handleCloseModal();
    };

    const isEditActionEnabled = isEditEnabled ?? isActionEnabled;

    return (
        <StyledVisitsFromResultContainer>
            <Flex
                gap="8px"
                alignItems="center"
            >
                <StyledChipContainer>
                    <LimitedChipItems
                        items={chipItems}
                        delimiter={translate("common.or").toLocaleUpperCase()}
                        maxItems={MAX_CHIPS_GROUP_AMOUNT}
                        hideActionButton
                    />
                </StyledChipContainer>

                {isEditActionEnabled ? (
                    <StyledIconButton
                        iconName="edit-icon"
                        iconSize="xs"
                        type="flat"
                        onClick={() => setIsModalOpened(true)}
                    />
                ) : null}
            </Flex>

            {!isSingleCountryMode && resolvedValue.length > 1 ? <AggregatedDataCheckbox /> : null}

            <VisitsFromSelectionModal
                selectedIds={resolvedValue}
                hasRadioOptions
                isOpened={isModalOpened}
                onApply={handleSelectionApply}
                onCancel={handleCloseModal}
                regions={filter.options.regions}
                countries={filter.options.countries}
            />
        </StyledVisitsFromResultContainer>
    );
};

export default VisitsFromFilterResult;
